/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import {
  buyBasicRequestAction,
  formatUsd,
  refreshBasicAction,
  toRaw,
} from '@root/entities/basic'
import { STORAGE_PREFIX } from '@root/shared'
import { ApiError } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

/**
 * Quick buy in Basic: one tap, a fixed number of dollars, out of the balance.
 *
 * The same order the panel on a token page sends, from every card and table
 * row that has a quick-buy button. The amount is dollars because Basic has
 * nothing else — no SOL to spend, no wallet to pick — and the button says the
 * amount on its face, which is what makes a single tap safe.
 */

/** What a quick buy spends unless the person has set otherwise. */
export const BASIC_QUICK_BUY_DEFAULT_USD = '10'

/** Chains a Basic buy can land on. TON is not among them. */
export const BASIC_QUICK_BUY_NETWORKS = new Set([
  'sol',
  'eth',
  'bsc',
  'base',
  'arbitrum',
  'robinhood',
])

/** As typed, and kept per browser: a quick-buy size is a habit. */
export const basicQuickBuyAmountRawAtom = atom(
  BASIC_QUICK_BUY_DEFAULT_USD,
  'basicQuickBuyAmountRawAtom',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/v1/basicQuickBuyUsd`))

/** The amount in dollars, or nothing while the field holds no amount. */
export const basicQuickBuyAmountAtom = atom((ctx) => {
  const amount = BigNumber(ctx.spy(basicQuickBuyAmountRawAtom))

  return amount.isFinite() && amount.isGreaterThan(0) ? amount : undefined
}, 'basicQuickBuyAmountAtom')

export const setBasicQuickBuyAmountRawAction = action((ctx, raw: string) => {
  basicQuickBuyAmountRawAtom(ctx, raw)
}, 'setBasicQuickBuyAmountRawAction')

/** Back to the default when the field is left with something unusable. */
export const resetBasicQuickBuyAmountAction = action((ctx) => {
  if (!ctx.get(basicQuickBuyAmountAtom)) {
    basicQuickBuyAmountRawAtom(ctx, BASIC_QUICK_BUY_DEFAULT_USD)
  }
}, 'resetBasicQuickBuyAmountAction')

/** Refusals the API names, said the way a person needs to hear them. */
const REFUSALS: Partial<
  Record<
    string,
    'basic.quick_buy_not_enough' | 'basic.quick_buy_balance_unavailable'
  >
> = {
  INSUFFICIENT_BALANCE: 'basic.quick_buy_not_enough',
  NO_DEPOSIT_ACCOUNT: 'basic.quick_buy_not_enough',
  BALANCE_UNAVAILABLE: 'basic.quick_buy_balance_unavailable',
}

export interface BasicQuickBuyTarget {
  tokenAddress: string
  /** The API's short name: "sol", "eth". */
  network: string
  symbol: string
}

/**
 * A quick-buy button's own request, so its spinner and tick belong to the
 * button that was pressed and not to every button on the page.
 */
export const reatomBasicQuickBuy = (target: BasicQuickBuyTarget) =>
  reatomRequest({
    name: `basicQuickBuy.${target.network}.${target.tokenAddress}`,
    execAction: action(async (ctx) => {
      const amount = ctx.get(basicQuickBuyAmountAtom)

      if (!amount) {
        return Result.fail(new Error('no quick-buy amount set'))
      }

      const result = await ctx.schedule(() =>
        buyBasicRequestAction(ctx, {
          tokenAddress: target.tokenAddress,
          network: target.network as never,
          amount: toRaw(amount),
        }),
      )

      if (Result.isSuccess(result)) {
        // Accepted, not bought: the order is queued and fills in a moment.
        // Saying "bought" here would be a claim the chain has not made yet.
        showSnackbarAction(ctx, {
          intent: 'success',
          text: i18next.t('basic.quick_buy_accepted', {
            amount: formatUsd(amount),
            symbol: target.symbol,
          }),
        })
        // The dollars are committed now; the balance has to say so before
        // the next tap, or the second tap spends them again.
        await ctx.schedule(() => refreshBasicAction(ctx))
      } else {
        const refusal = ApiError.is(result.failure)
          ? REFUSALS[result.failure.code]
          : undefined

        showSnackbarAction(ctx, {
          intent: 'error',
          text: i18next.t(refusal ?? 'basic.quick_buy_refused'),
        })
      }

      return result
    }, `basicQuickBuy.${target.network}.${target.tokenAddress}.execute`),
  })
